import styled, { css } from 'styled-components';

import { mediaQuery } from '../../styles/mixin';
import { Button, ButtonSize } from '../Button';
import { Icon, IconName } from '../Icon';
import { LoadingSpinner } from '../Loading/styled';
import { Tag } from '../Tag';
import { H2 } from '../Typography/Headings';

export interface IContainerProps {
    isLoading?: boolean;
    backgroundOpacity?: number;
    disableOnHover?: boolean;
}

export const Container = styled.li<IContainerProps>`
    display: grid;
    position: relative;
    row-gap: 0.25rem;
    align-items: flex-start;
    grid-template-columns: auto 2.125rem;
    grid-template-rows: 1.4rem 2rem auto;
    grid-template-areas:
        '. badge'
        'name name'
        'worth worth'
        'place-cata place-cata'
        'checkbox checkbox';
    width: 17%;
    height: 12rem;
    min-width: 10rem;
    max-height: 15rem;
    ${({ backgroundOpacity }) =>
        `background: rgba(255, 255, 255, ${backgroundOpacity ?? 1})`};
    box-shadow: ${({ theme }) => theme.boxShadow.primary};
    border-radius: 0.75rem;
    padding: 1rem;
    cursor: ${({ disableOnHover }) => (disableOnHover ? 'default' : 'pointer')};
    transition: box-shadow ${({ theme }) => theme.transitions.hover};
    ${({ disableOnHover }) =>
        !disableOnHover &&
        css`
            &:hover {
                box-shadow: ${({ theme }) => theme.boxShadow.primaryHover};
            }
        `}

    &::after {
        content: ${({ isLoading }) => isLoading && '""'};
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: #0000003d;
        pointer-events: none;
        border-radius: 0.75rem;
    }

    ${mediaQuery(
        2,
        css`
            padding: 0.625rem 0.625rem 1.3rem 1.3rem;
            width: 18%;
            max-width: 15rem;
        `
    )};
`;

export const CardSpinner = styled(LoadingSpinner)`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 100;
    margin: 0;
`;

export const StyledNewTag = styled(Tag)`
    display: none;
    ${mediaQuery(
        2,
        css`
            display: flex;
            justify-content: center;
            align-items: center;
            grid-area: badge;
            padding: 0.375rem;
        `
    )}
`;

export const StyledNewMobileTag = styled.div`
    width: 0.5rem;
    height: 0.5rem;
    background: ${({ theme }) => theme.colors.logoPrimary};
    border-radius: 50%;
    justify-self: flex-end;
    align-self: center;
    ${mediaQuery(
        2,
        css`
            display: none;
        `
    )}
`;

export const Name = styled(H2)`
    grid-area: name;
    align-self: flex-end;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
`;

export const Money = styled(H2)`
    grid-area: worth;
    font-weight: 700;
    letter-spacing: 0.01625em;
    color: ${({ theme }) => theme.colors.logoPrimary};
`;

export const PlaceCataContainer = styled.div`
    grid-area: place-cata;
`;

export const CheckboxContainer = styled.div`
    grid-area: checkbox;
    display: flex;
    flex-direction: row;
    align-self: center;
    justify-content: flex-start;
    align-items: flex-end;
    font-size: ${({ theme }) => theme.fontSizes[1]};
    column-gap: 0.3rem;
    margin-top: 0.5rem;
    ${mediaQuery(
        2,
        css`
            margin-top: 0rem;
        `
    )}
`;

interface IFooterButtonProps {
    color: string;
}

export const FooterButton = styled(Button).attrs({
    size: ButtonSize.SMALL,
})<IFooterButtonProps>`
    position: absolute;
    bottom: 0;
    right: 0;
    left: 0;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    font-weight: normal;
    background: transparent;
    border-color: transparent;
    border-top-color: ${({ theme }) => theme.colors.lightShadow};
    color: ${({ theme }) => theme.colors.fontPrimary};
    font-size: ${({ theme }) => theme.fontSizes[3]};
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    white-space: nowrap;

    &:hover {
        color: white;
        background: ${({ color }) => color};
        border-color: ${({ color }) => color};
    }

    &:active,
    &:focus {
        font-weight: 600;
        color: white;
        background: ${({ color }) => color};
        border-color: ${({ color }) => color};
    }
`;

export const StyledIcon = styled(Icon).attrs({ iconName: IconName.ARROW })`
    flex-shrink: 0;
    margin: 0;
    margin-left: 0.125rem;
    width: 0.75rem;
    height: 0.75rem;
`;
